// The AUT block a caller may send: CODE V 2026 Update 0.1 Optimization Reference
// command summary (PromptingGuide pp95–116), its Surface Parameters Constraints
// table (Opt_5_Constraints.06.13) and user-defined constraints
// (Opt_5_Constraints.06.17–06.19). Full Macro-PLUS was approved for error
// functions (2026-11-01); the boundary is that a line may only be an AUT
// command or constraint, never a file, session or other-option command.
//
// Refused although the summary lists them, each because the worker owns it:
// MXC and TIM (the run budget, sent before the block: MXC is last-wins and an
// MNC above the current MXC is reset to it), INT (stops at every cycle for
// input), DRA and CHT (drawings and chart templates), WGF and WHF (graphics and
// chart files), WBF (buffers outlive the request), AOF (output file), GS (saves
// a lens file per solution in the working directory, Opt_3_Optimization.04.5)
// and GO (sent by the worker).

const variableControls = [
  'CCM', 'CCY', 'CCX', 'THC', 'GLC', 'GC1', 'GC2', 'GRC', 'HCC', 'UCC', 'BLC', 'ROC', 'RSC', 'RXC', 'RYC', 'RZC',
  'KC', 'AC', 'BC', 'CC', 'DC', 'EC', 'FC', 'GC', 'HC', 'JC', 'CCF', 'SC0', 'SC1', 'SC2', 'SC3', 'SC4',
  'KYC', 'ARC', 'BRC', 'CRC', 'DRC', 'KXC', 'APC', 'BPC', 'CPC', 'DPC',
  'CX1', 'CY1', 'CZ1', 'CI1', 'CX2', 'CY2', 'CZ2', 'CI2',
  'FLC', 'RDC', 'FFC', 'BFC', 'EPC', 'FDC', 'EDC', 'MCC', 'SCC',
  'XDC', 'YDC', 'ZDC', 'XOC', 'YOC', 'ZOC', 'ADC', 'BDC', 'CDC', 'UMC',
  'DEF', 'VAR', 'FRZ', 'DER',
];
const errorFunctionControls = [
  'CVP', 'STP', 'DIF', 'ROU', 'BFG', 'CNV', 'EXT', 'SAB', 'ASB', 'DMD', 'CEN', 'WFR', 'WVB', 'ERR',
  'WTA', 'WTF', 'WTX', 'WTY', 'WTW', 'TFO', 'WTT', 'CPA', 'FAP', 'DEL', 'OBS', 'SAP', 'NRR', 'NSP',
  // MTF optimization (p103) and CEF optimization (pp104–105).
  'FRE', 'AZI', 'TGM', 'WTM', 'WTP', 'NRD',
  'MPR', 'CED', 'DBP', 'TGR', 'GRI', 'WRX', 'WRY', 'RCX', 'RCY',
  // User-defined error function components (pp106–108).
  'ENT', 'ABR', 'ABL', 'APL', 'ABC', 'AWF', 'AWW',
  'DSP', 'PTC', 'WTC', 'RAY',
  // Listing controls (pp112–113).
  'VLI', 'RSP', 'SUR', 'CHG', 'EFP', 'CHR', 'DCS',
  // Asphere conversion (pp115–116).
  'NAS', 'NTM', 'SLO', 'CVX', 'CCV', 'DIA', 'SNG', 'ASA', 'EXC',
];
// The specific constraints; AUT accepted all 289 catalog forms over these codes
// on the test worker (2026-11-01, captured in #8132).
const constraints = [
  'A', 'ADE', 'AFN', 'AOI', 'AOR', 'AP', 'AR', 'ASC', 'ATC', 'ATE', 'AX', 'B', 'BDE', 'BLO', 'BP', 'BR', 'BSC',
  'C', 'CDE', 'CP', 'CR', 'CSC', 'CT', 'CUF', 'CUX', 'CUY', 'D', 'DEP', 'DIX', 'DIY', 'DP', 'DR', 'DST', 'E',
  'EFL', 'EFX', 'EFY', 'ENP', 'ET', 'EXP', 'F', 'FPX', 'FPY', 'G', 'GLA', 'GRN', 'H', 'HCO', 'HCX', 'HCY', 'HFA',
  'HFR', 'HMX', 'HMY', 'HX1', 'HX2', 'HY1', 'HY2', 'HZ1', 'HZ2', 'ICX', 'ICY', 'IMC', 'IMD', 'IMX', 'IMY', 'J',
  'K', 'KX', 'KY', 'L', 'LAT', 'LSC', 'M', 'MAE', 'MBF', 'MCO', 'MED', 'MEN', 'MFD', 'MFF', 'MFL', 'MNA', 'MNE',
  'MNT', 'MRD', 'MSC', 'MXA', 'MXT', 'N', 'NRF', 'NSC', 'OAL', 'OP', 'OPD', 'PTB', 'PTZ', 'QSG', 'QSL', 'RDX',
  'RDY', 'RED', 'SA', 'SAS', 'SCO', 'SD', 'SN2', 'SNA', 'SNS', 'SP0', 'SP1', 'SP2', 'SP3', 'SP4', 'SRL', 'SRM',
  'SRN', 'TAS', 'TCO', 'THI', 'TT', 'UCO', 'UCX', 'UCY', 'UMX', 'UMY', 'X', 'XCN', 'XDE', 'XFO', 'XOD', 'XSC',
  'Y', 'YCN', 'YDE', 'YFO', 'YNI', 'YOD', 'YSC', 'Z', 'ZDE', 'ZOD', 'ZSC',
];
// Macro-PLUS pp35–41, "Predefined Functions": deterministic math, and optical
// functions that return a scalar from numeric arguments. Image, array, file,
// message and ray-trace-into-database functions are not callable.
const udcFunctions = [
  'ABSF', 'ACOSF', 'ASINF', 'ATANF', 'ATAN2F', 'COSF', 'EXPF', 'LOGF', 'LOG10F', 'MAXF', 'MINF', 'MODF',
  'ROUNDF', 'SINF', 'SQRTF', 'TANF', 'SIGNF',
  'BESTSPH', 'CNVTFIELD', 'EVALZERN', 'FITERROR', 'INDEX', 'MTF_1FLD', 'NORMRADIUS', 'RMSSPOT', 'SAGF',
  'ZERNIKE', 'ZERNIKEGQ', 'ZFRCOEF',
];

// Every token below ends where the next cannot continue it, so a line has one
// tokenization and the match runs in linear time (a digit run that could split
// between repetitions backtracked exponentially).
const name = '@[A-Za-z0-9_]{1,31}(?![A-Za-z0-9_])';
// Qualifiers, values and comparison targets; GRC's glass label is quoted (p95).
const token = `(?:[A-Za-z0-9_.+-]+|[<>=]|${name}|'[A-Za-z0-9_.-]{1,8}')`;
const freeFormWords = [...variableControls, ...errorFunctionControls, ...constraints];
// A single-letter constraint always carries qualifiers or a target; bare, `N` and
// `Y` would be the replies that end or continue AUT at its prompts.
const words = (length: (n: number) => boolean) => freeFormWords.filter((word) => length(word.length)).join('|');
const commandLine = `(?:(?:${words((n) => n > 1)})(?: +${token})*|(?:${words((n) => n === 1)})(?: +${token})+)`;
// Exit criteria the caller may set (the worker owns MXC and TIM), each with the
// arguments of Specifying Exit Criteria: MNC num_of_cycles_min, TAR
// lower_lim_err_fun [upper_lim_err_fun], IMP min_impr_factor. With free tokens,
// `MNC 3 S6 50` passed and CODE V took it without a warning (test worker, 2026-11-02).
const exitControls = ['MNC', 'TAR', 'IMP'];
const number = '[+-]?(?:[0-9]+(?:\\.[0-9]*)?|\\.[0-9]+)(?:[eE][+-]?[0-9]+)?';
const exitLine = `(?:MNC +[0-9]+|TAR +${number}(?: +${number})?|IMP +${number})`;
export const AUT_COMMAND_WORDS = [...freeFormWords, ...exitControls];
// A UDC follows the user-defined function rules (Opt_5_Constraints.06.18): a word
// is a database-item keyword unless an allowed function name opens a call. A
// sign is the operator token (the unary minus, .06.18), and `**` is two `*`.
const unsignedNumber = '(?:[0-9]+(?:\\.[0-9]*)?|\\.[0-9]+)(?:[eE][+-]?[0-9]+)?(?![A-Za-z0-9_.])';
const expressionToken = [
  // A name followed by `(` would call a user-defined Macro-PLUS function, which
  // CODE V looks up as a macro file; only the listed functions are callable.
  `(?:${udcFunctions.join('|')}) *\\(`, `[A-Za-z][A-Za-z0-9_]*(?![A-Za-z0-9_]| *\\()`, unsignedNumber, `${name}(?! *\\()`,
  "'[A-Za-z0-9_ .-]{0,32}'", '[-+*/(),]', ' ',
].join('|');
const udcDefinition = `${name} ?== ?(?:${expressionToken})+`;
const udcConstraint = `${name}(?: +${token})+`;

export const autLines = [exitLine, commandLine, udcDefinition, udcConstraint];
